import { useEffect, useRef, useState } from "react";
import { formatDateTime } from "@/lib/format";
import { TiltCard } from "@/components/TiltCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const primaryBtn =
  "w-full rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 px-4 py-3 text-sm font-semibold tracking-wide text-white shadow-lg shadow-indigo-500/25 transition hover:from-indigo-400 hover:to-violet-500 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60";

export function UploadView() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [batches, setBatches] = useState([]);
  const inputRef = useRef(null);

  const loadBatches = () => {
    fetch(`${API}/uploads`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setBatches(d.items || []))
      .catch(() => {});
  };

  useEffect(loadBatches, []);

  const downloadTemplate = () => {
    const csv =
      "nama,nomor_whatsapp,nominal,catatan\nAndi,08123456789,1500000,Contoh catatan\nBudi,08987654321,500000,\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "template_pembelian.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleUpload = async () => {
    if (!file || uploading) return;
    setUploading(true);
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/purchases/upload`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error("upload failed");
      setResult(data);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      loadBatches();
    } catch (e) {
      setError("Upload gagal. Periksa format file lalu coba lagi.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <TiltCard
        data-testid="upload-card"
        className="glass enter-up rounded-2xl px-6 py-8"
      >
        <h2 className="text-lg font-extrabold tracking-tight text-white">Upload</h2>
        <p className="mt-0.5 text-sm text-indigo-200/60">
          Upload CSV/Excel. Tiap baris dikirim ke Meta CAPI.
        </p>

        <div className="mt-5 rounded-lg border border-white/10 bg-white/5 px-4 py-3">
          <p className="text-xs font-semibold text-indigo-100/80">Format kolom file:</p>
          <p
            data-testid="upload-format-hint"
            className="mt-1 font-mono text-xs text-indigo-200/60"
          >
            nama, nomor_whatsapp, nominal, catatan (opsional)
          </p>
          <button
            type="button"
            data-testid="download-template-button"
            onClick={downloadTemplate}
            className="mt-2 text-xs font-semibold text-indigo-300 underline underline-offset-2 hover:text-indigo-100"
          >
            Unduh template CSV
          </button>
        </div>

        <div className="mt-4">
          <input
            ref={inputRef}
            id="upload-file"
            data-testid="upload-file-input"
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => {
              setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
              setResult(null);
              setError(null);
            }}
            className="w-full rounded-xl border border-dashed border-white/20 bg-white/5 px-3.5 py-3 text-sm text-indigo-100 file:mr-3 file:rounded-lg file:border-0 file:bg-gradient-to-r file:from-indigo-500 file:to-violet-600 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:from-indigo-400 hover:file:to-violet-500"
          />
          {file && (
            <p data-testid="upload-selected-file" className="mt-1.5 text-xs text-indigo-200/60">
              File dipilih: {file.name}
            </p>
          )}
        </div>

        {error && (
          <div
            data-testid="upload-error-message"
            className="mt-4 rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200"
          >
            {error}
          </div>
        )}

        {result && (
          <div
            data-testid="upload-result"
            className="mt-4 rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm"
          >
            <p className="font-semibold text-white">
              {result.success} berhasil, {result.failed} gagal dari {result.total} baris
            </p>
            {result.truncated && (
              <p
                data-testid="upload-truncated-warning"
                className="mt-1 text-xs text-amber-300"
              >
                File melebihi 500 baris. Hanya 500 baris pertama diproses.
              </p>
            )}
            {result.failed_rows && result.failed_rows.length > 0 && (
              <ul
                data-testid="upload-failed-rows"
                className="mt-2 space-y-1 text-xs text-red-300"
              >
                {result.failed_rows.map((r) => (
                  <li key={r.line}>
                    Baris {r.line}: {r.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <button
          type="button"
          data-testid="upload-submit-button"
          onClick={handleUpload}
          disabled={!file || uploading}
          className={`${primaryBtn} mt-5`}
        >
          {uploading ? "Memproses…" : "Proses upload"}
        </button>
      </TiltCard>

      <TiltCard
        data-testid="upload-history-card"
        className="glass enter-up rounded-2xl px-6 py-6"
      >
        <h3 className="text-sm font-bold tracking-wide text-white">Riwayat upload</h3>
        {batches.length === 0 ? (
          <p data-testid="upload-history-empty" className="mt-3 text-xs text-indigo-200/40">
            Belum ada upload.
          </p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table
              data-testid="upload-history-table"
              className="w-full text-left text-xs"
            >
              <thead>
                <tr className="border-b border-white/10 text-indigo-200/50">
                  <th className="py-2 pr-3 font-semibold">Waktu</th>
                  <th className="py-2 pr-3 font-semibold">File</th>
                  <th className="py-2 pr-3 font-semibold">Berhasil</th>
                  <th className="py-2 font-semibold">Gagal</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr
                    key={b.id}
                    className="border-b border-white/5 text-indigo-100/90"
                  >
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {formatDateTime(b.created_at)}
                    </td>
                    <td className="max-w-[140px] truncate py-2 pr-3">{b.filename}</td>
                    <td className="py-2 pr-3 font-semibold text-emerald-300">{b.success}</td>
                    <td className="py-2 font-semibold text-red-300">{b.failed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </TiltCard>
    </div>
  );
}
